/**
 * 模組頁面代碼 : 1320-UI-02
 * 頁面說明 : 控制信號新增修改視窗
 * @module web/modules/role-permission/components/ControlCodeModal
 */

// ----- import from npm modules -----
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

// ----- constants -----
const CONTROL_CODE_NEW = 'new';
const CONTROL_CODE_EDIT = 'edit';

const RESPONSE_OK = 200;
const TITLE_CONTROL_CODE_NEW = '新增控制信號';
const TITLE_CONTROL_CODE_EDIT = '修改控制信號';

const ControlCodeModal = React.forwardRef((props, ref) => {
    const { mode, controlCode, searchControlCode } = props;

    let modalIcon = mode === CONTROL_CODE_NEW ? '#ico-plus' : '#ico-edit';
    let modalTitleText = mode === CONTROL_CODE_NEW ? TITLE_CONTROL_CODE_NEW : TITLE_CONTROL_CODE_EDIT;

    // 初始化state
    const [formValues, setFormValues] = useState({
        modalControlId: '',
        modalControlCode: '',
        modalControlType: '',
        modalFloorNum: '',
        modalControlMessage: '',
        modalControlMessageDisplay: '',
    });

    const [invalidShow, setInvalidShow] = useState('none');

    // Handle change event
    const handleChange = (event) => {
        const target = event.target;
        let value = target.value;
    
        if (target.name === 'modalControlMessage' || target.name === 'modalFloorNum') {
            let displayValue = target.name === 'modalControlMessage' ? value : formValues.modalControlMessage;
            const floorNum = target.name === 'modalFloorNum' ? parseInt(value || 0) : parseInt(formValues.modalFloorNum || 0);

            setFormValues({
                ...formValues,
                [target.name]: value,
                modalControlMessageDisplay: DOMPurify.sanitize(displayValue),
            });
            return;
        }
    
        setFormValues({
            ...formValues,
            [target.name]: value,
        });
    };

    // 使用useEffect在controlCode改變才更新formValues
    useEffect(() => {
        let displayCodeMessageDisplay = controlCode ? controlCode.controlMessage : '';
        
        setFormValues({
            modalControlId: controlCode ? controlCode.controlId : '',
            modalControlCode: controlCode ? controlCode.controlCode : '',
            modalControlType: controlCode ? controlCode.controlType : '',
            modalFloorNum: controlCode ? controlCode.floorNum : '',
            modalControlMessage: controlCode ? controlCode.controlMessage : '',
            modalControlMessageDisplay: displayCodeMessageDisplay !== '' ? DOMPurify.sanitize(displayCodeMessageDisplay) : '',
        });
        setInvalidShow('none');
    }, [controlCode]);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;
    
        // 建立 FormData 讀取表单的值
        const formData = new FormData(form);
    
        // 檢查表单是否验证通过
        const controlType = formData.get('modalControlType');
        const floorNum = parseInt(formData.get('modalFloorNum') || 0);
        if (controlType === 'HU' || controlType === 'HD' || controlType === 'C' || controlType === 'CL') {
            if (floorNum === 0 || floorNum === null || floorNum === undefined) {
                setInvalidShow('block');
                return;
            }
        }
    
        if (form.checkValidity() === false) {
            setInvalidShow('block');
            form.classList.add('was-validated');
            return;
        }
    
        // 建立 controlCode 物件
        const saveControlCode = {
            controlId: mode === CONTROL_CODE_NEW ? null : controlCode.controlId,
            controlCode: formData.get('modalControlCode'),
            controlType: formData.get('modalControlType'),
            floorNum: parseInt(formData.get('modalFloorNum')),
            controlMessage: formData.get('modalControlMessage'),
        };
    
        // 呼叫父層元件的新增或修改控制信號函式
        if (mode === CONTROL_CODE_NEW) {
            // 调用 props.addControlCode 方法
            props.addControlCode(saveControlCode).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_CONTROL_CODE_NEW, response.payload);
                } else {
                    // 调用 fetchControlCodeList and Hide the modal
                    const { controlCode, controlType } = searchControlCode;
                    props.fetchControlCodeList(controlCode, controlType).then(() => {
                        props.setNeedControlCodeLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        } else if (mode === CONTROL_CODE_EDIT) {
            // 调用 props.editControlCode 方法
            props.editControlCode(saveControlCode).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_CONTROL_CODE_EDIT, response.payload);
                } else {
                    // 调用 fetchControlCodeList and Hide the modal
                    const { controlCode, controlType } = searchControlCode;
                    props.fetchControlCodeList(controlCode, controlType).then(() => {
                        props.setNeedControlCodeLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        }
    };

    return (
        <div ref={ref}
            className="modal fade"
            id="modalCtrlCode"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalCtrlCodeLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalRolePermForm" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalCtrlCodeLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href={modalIcon}></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">{modalTitleText}</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">信號基本資料</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-9">
                                            <label htmlFor="modalControlCode" className="form-label">信號代碼*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalControlCode"
                                                name="modalControlCode"
                                                value={formValues.modalControlCode}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalControlType" className="form-label">信號類型*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalControlType"
                                                name="modalControlType"
                                                value={formValues.modalControlType || ""}
                                                onChange={handleChange}
                                                required>
                                                    <option disabled value="">請選擇</option>
                                                    <option key="1" value="HU">乘場叫車上樓</option>
                                                    <option key="2" value="HD">乘場叫車下樓</option>
                                                    <option key="3" value="C">車廂控制</option>
                                                    <option key="4" value="DU">車廂行進方向(上)</option>
                                                    <option key="5" value="DD">車廂行進方向(下)</option>
                                                    <option key="6" value="CL">車廂所在樓層</option>
                                                    <option key="7" value="DC">關門</option>
                                                    <option key="8" value="DO">開門</option>
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalFloorNum" className="form-label">控制樓層</label>
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-control form-control-sm"
                                                id="modalFloorNum"
                                                name="modalFloorNum"
                                                value={formValues.modalFloorNum}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="modalFloorNum" className="form-label"><span style={{ color: 'red' }}>類型為乘場叫車、車廂控制、車廂所在樓層，此欄位必填</span></label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">信號顯示</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-9">
                                            <label htmlFor="modalControlMessage" className="form-label">設定表示訊息</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalControlMessage"
                                                name="modalControlMessage"
                                                value={formValues.modalControlMessage}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="modalControlMessage" className="form-label"><span style={{ color: 'red' }}>訊息表示：#UP#–上樓；#DN#–下樓；#TO#–到；%s–樓層</span></label>
                                        </div>
                                        <div className="col-3">
                                            <label htmlFor="modalControlMessageDisplay" className="form-label">顯示訊息</label>
                                            <div
                                                className="form-control form-control-sm"
                                                id="modalControlMessageDisplay"
                                                name="modalControlMessageDisplay">
                                                {formValues.modalControlMessageDisplay.split('\n').map((line, index) => (
                                                    <React.Fragment key={index}>
                                                        {line.split(/(#UP#|#DN#|#TO#|%s)/g).map((part, i) => {
                                                            if (part === '#UP#') return <i key={i} className="bi bi-caret-up-fill" style={{ color: 'red' }}></i>;
                                                            if (part === '#DN#') return <i key={i} className="bi bi-caret-down-fill" style={{ color: 'green' }}></i>;
                                                            if (part === '#TO#') return <i key={i} className="bi bi-arrow-right" style={{ color: 'orange' }}></i>;
                                                            if (part === '%s') return formValues.modalFloorNum;
                                                            return part;
                                                        })}
                                                        <br />
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <div className="alert alert-warning global-invalid-feedback" style={{ display: invalidShow }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default ControlCodeModal;